import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { RootState, useAppDispatch, useAppSelector } from "../../../state/state.tsx";
import { CircularProgress, IconButton } from '@mui/material';
import { closeModal, toggleLoading } from '../../../state/ModalSlice/ModalSlice.ts';
import CloseIcon from "@mui/icons-material/Close";
import { useBackend } from '../../../hooks/backend/useBackend.ts';
import { db } from '../../../Config/firebase.ts';
import { toast } from 'react-toastify';
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2.ts';

import { doc, getDoc, Timestamp } from 'firebase/firestore';


export default function MultiMonthResumeModal() {

    const Dispatch = useAppDispatch();
    const isLoading = useAppSelector((state: RootState) => state.ModalSlice.isLoading)
    const open = useAppSelector((state: RootState) => state.ModalSlice.MultiMonthResumeModalOpen)
    const ServiceProviderID: string = useAppSelector(selectEffectiveUID) 
    const { LiveSubscriptionId, align2monthlyBoundary} = useAppSelector((state: RootState) => state.ArbitrarySlice.ForMultiMonthResumeModal)
    const LiveSubs = useAppSelector((state: RootState) => state.LiveSubscriptionSlice.data)
    const selectedLiveSub = LiveSubs.filter((data) => data.id === LiveSubscriptionId)[0]
    // HandleChange Functions̥


    function handleClose() {
   
        Dispatch(closeModal("MultiMonthResumeModalOpen"))
    }

    // On Submit operation

    const { update } = useBackend(`/ServiceProviders/${ServiceProviderID}/LiveSubscriptions`)

    // Add helper function to get appropriate message
    const getResumeMessage = () => {
        if (align2monthlyBoundary && selectedLiveSub.billingCycle!== "FlexibleMonthly") {
            return "Resuming will rollback the existing pause, and student will be able to access all the services and invoices again. Are you sure?";
        }
        return "Student is paused. Do you want to resume the student? Confirming will resume the subscription and the student will be able to access the services again.";
    };

    async function onSubmit() {
        Dispatch(toggleLoading(true))
        try {
            const docRef = doc(db, `ServiceProviders/${ServiceProviderID}/LiveSubscriptions`, LiveSubscriptionId);
            const docSnap = await getDoc(docRef);
            
            if (docSnap.exists()) {
                const data = docSnap.data();
                const pauseHistory = data.pauseHistory || [];
                const currentPauseDaysLeft = data.pauseDaysLeft || 0;
                const currentBillingEnd = data.billingEnd ? data.billingEnd.toDate() : new Date();
                
                if (pauseHistory.length > 0) {
                    if (align2monthlyBoundary && selectedLiveSub.billingCycle!== "FlexibleMonthly") {
                        // Calculate days between pauseTS and resumeTS for the last entry
                        const lastPauseEntry = pauseHistory[pauseHistory.length - 1];
                        let diffInDays = 0;
                        
                        if (lastPauseEntry.pauseTS && lastPauseEntry.resumeTS) {
                            const pauseDate = lastPauseEntry.pauseTS.toDate();
                            const resumeDate = lastPauseEntry.resumeTS.toDate();
                            const diffInMs = resumeDate.getTime() - pauseDate.getTime();
                            diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24)) + 1;
                        }

                        // Remove the last pause entry and set billing end to last day of previous month
                        const updatedPauseHistory = [...pauseHistory];
                        updatedPauseHistory.pop();
                        const newBillingEnd = new Date(currentBillingEnd.getFullYear(), currentBillingEnd.getMonth(), 0);
                        
                        await update(LiveSubscriptionId, {
                            status: "Active",
                            pauseHistory: updatedPauseHistory,
                            pauseDaysLeft: currentPauseDaysLeft + diffInDays,
                            billingEnd: Timestamp.fromDate(newBillingEnd),
                            updatedAt: Timestamp.fromDate(new Date())
                        });
                    } else {
                        // Existing logic for non-aligned case
                        const lastPauseEntry = pauseHistory[pauseHistory.length - 1];
                        const updatedPauseHistory = [...pauseHistory];
                        const now = Timestamp.now();
                        
                        // Calculate days between previous resumeTS and now
                        if (lastPauseEntry.resumeTS) {
                            const prevResumeDate = lastPauseEntry.resumeTS.toDate();
                            const todayDate = now.toDate();
                            const diffInDays = Math.floor((prevResumeDate.getTime() - todayDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                            
                            // Adjust billing end date by removing unused pause days
                            const newBillingEnd = new Date(currentBillingEnd);
                            newBillingEnd.setDate(newBillingEnd.getDate() - diffInDays);
                            
                            // Update the pause history, pauseDaysLeft and billingEnd
                            updatedPauseHistory[updatedPauseHistory.length - 1].resumeTS = now;
                            await update(LiveSubscriptionId, { 
                                status: "Active",
                                pauseHistory: updatedPauseHistory,
                                pauseDaysLeft: currentPauseDaysLeft + diffInDays,
                                billingEnd: Timestamp.fromDate(newBillingEnd),
                                updatedAt: Timestamp.fromDate(new Date())
                            });
                        } else {
                            // If no previous resumeTS exists
                            updatedPauseHistory[updatedPauseHistory.length - 1].resumeTS = now;
                            await update(LiveSubscriptionId, { 
                                status: "Active",
                                pauseHistory: updatedPauseHistory
                            });
                        }
                    }
                } else {
                    const updatedPauseHistory = [{  
                        resumeTS: Timestamp.now()
                    }]
                    await update(LiveSubscriptionId, { 
                        status: "Active",
                        pauseHistory: updatedPauseHistory
                    });
                }
            }

            toast.success("Student resumed successfully");
        } catch (error) {
            toast.error("Failed to resume student");
        }

        Dispatch(toggleLoading(false))
        handleClose()

    }


    return (
        <Dialog
            sx={{
                padding: 0,
                '& .MuiPaper-root': {
                    borderRadius: '10px',
                },

            }}
            maxWidth="md"
            open={open}
            disableEscapeKeyDown
            PaperProps={{
                component: 'div',
            }}
        >
            <DialogContent sx={{ paddingX: "60px", paddingY: "40px" }} className={"bg-white"}>
                <div className={"header pb-[20px] min-w-[550px]"}>
                    <p className={"text-cf_heading font-[600] "}>Resume Student</p>
                </div>

                <div className={"absolute right-4 top-4"}><IconButton onClick={handleClose}>
                    <CloseIcon />
                </IconButton></div>

                <div className={"pt-0"}>
                    <p>{getResumeMessage()}</p>
                </div>

                <div className={"flex gap-2 pt-8"}>
                <button onClick={handleClose} disabled={isLoading}
                            className={`w-full bg-cc_bg-tertiary border border-solid border-gray-500 p-1.5 rounded-lg ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}>Cancel
                    </button>
                    <button
                        onClick={onSubmit} disabled={isLoading}
                        className={`w-full bg-cc_brand text-white p-1.5 rounded-lg border-gray-500 flex justify-center items-center ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}>{isLoading &&
                            <CircularProgress sx={{ color: "white" }} size={20} />}
                        <p className={"ml-2"}>{isLoading ? "Loading" : "Confirm"}</p>
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}